import React from "react";
import { ModalBase } from "@aspen/ui";
import { SUB_ACCOUNT_AUTHORIZATION_MODULE } from "@aspen/model";
import type { ISubAccountAuthorizationModule } from "@aspen/model";
import { hasPermissionOrNotSubAccount, i18nUtil } from "@aspen/libs";

/**
 * 子账号权限提示弹窗组件
 * @description 显示子账号的操作权限列表，包括权限名称、描述和状态（查看/操作）
 */
interface IModalSubAccountPermissionTipsProps {
  /** 是否显示弹窗 */
  visible: boolean;
  /** 关闭弹窗回调 */
  onClose: () => void;
}

/**
 * 权限项组件
 * @param config 权限配置
 * @param intl 多语言对象
 */
const PermissionItem: React.FC<{
  config: ISubAccountAuthorizationModule;
  intl: Record<string, string>;
}> = ({ config, intl }) => {
  const hasPermission = hasPermissionOrNotSubAccount(config.key);

  // 根据权限状态决定显示的标签
  // 有权限显示 Operate，没有权限显示 View
  const statusKey = hasPermission
    ? "subaccount.permission.status.operate"
    : "subaccount.permission.status.view";

  return (
    <div className="box-border flex flex-col items-start mb-[-1px] w-full">
      <div className="border border-white/10 border-solid box-border flex gap-[24px] items-start px-[24px] py-[16px] w-full">
        <div className="flex gap-[12px] items-center shrink-0 w-[64px]">
          <p className="font-common-medium leading-[22px] text-[14px] text-white/85">
            {intl[config.label]}
          </p>
        </div>
        <p className="flex-1 leading-[22px] text-[14px] text-white/85">
          {intl[config.descriptionKey]}
        </p>
        <div className="flex gap-[12px] items-center justify-center shrink-0 w-[56px]">
          <p className="font-common-medium leading-[22px] text-[14px] text-white/85 text-center whitespace-pre">
            {intl[statusKey]}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * 子账号权限提示弹窗
 */
export const ModalSubAccountPermissionTips: React.FC<IModalSubAccountPermissionTipsProps> = ({
  visible,
  onClose,
}) => {
  const intl = i18nUtil.t();

  return (
    <ModalBase
      open={visible}
      onCancel={onClose}
      title={intl["subaccount.permission.modal.title"]}
      footer={null}
      width={480}
      className="subaccount-permission-modal">
      <div className="box-border flex flex-col items-start pb-px pt-0 px-0 w-full">
        {SUB_ACCOUNT_AUTHORIZATION_MODULE.map(config => (
          <PermissionItem key={config.key} config={config} intl={intl} />
        ))}
      </div>
    </ModalBase>
  );
};

export default ModalSubAccountPermissionTips;
